import { StateCreator } from "zustand";

import {
  UI_SIZE_LARGE_BREAKPOINT,
  UI_SIZE_SMALL_BREAKPOINT,
  UI_SIZE_XSMALL_BREAKPOINT
} from "../../constants";
import { PlayerUiType, UI_SIZE } from "../../enums";
import { SettingsName } from "../../types/common";
import { PlayerStoreProperties } from "../store";
import { StoreMiddlewares } from "../storeTypes";

export type UiSliceState = {
  controlsSize: UI_SIZE;
  errorOverlayVisible: boolean;
  isCasting: boolean;
  isChatButtonEnabled: boolean;
  isControlsVisible: boolean;
  isDraggingTimeline: boolean;
  isFullscreen: boolean;
  isPictureInPicture: boolean;
  playerElement: Element | undefined;
  playerUiType: PlayerUiType;
  uiSize: UI_SIZE;
  uiSizeInPx: number;
  visibleSettingsMenu: SettingsName | undefined;
};

type UiSliceActions = {
  setErrorOverlayVisible: (errorOverlayVisible: boolean) => void;
  setIsCasting: (isCasting: boolean) => void;
  setIsChatButtonEnabled: (isChatButtonEnabled: boolean) => void;
  setIsControlsVisible: (isControlsVisible: boolean) => void;
  setIsDraggingTimeline: (isDraggingTimeline: boolean) => void;
  setIsFullscreen: (isFullscreen: boolean) => void;
  setIsPictureInPicture: (isPictureInPicture: boolean) => void;
  setPlayerUiType: (playerUiType: PlayerUiType) => void;
  setUiSize: (uiSizeInPx: number) => void;
  setVisibleSettingsMenu: (
    visibleSettingsMenu: SettingsName | undefined
  ) => void;
};

export type UiSlice = UiSliceState & UiSliceActions;

export const UI_SLICE_DEFAULTS: UiSliceState = {
  controlsSize: UI_SIZE.SMALL,
  errorOverlayVisible: false,
  isCasting: false,
  isChatButtonEnabled: false,
  isControlsVisible: true,
  isDraggingTimeline: false,
  isFullscreen: false,
  isPictureInPicture: false,
  playerElement: undefined,
  playerUiType: PlayerUiType.default,
  uiSize: UI_SIZE.DEFAULT,
  uiSizeInPx: 0,
  visibleSettingsMenu: undefined
};

export const uiSlice: StateCreator<
  PlayerStoreProperties,
  StoreMiddlewares,
  [],
  UiSlice
> = set => ({
  ...UI_SLICE_DEFAULTS,
  setErrorOverlayVisible: errorOverlayVisible =>
    set({ errorOverlayVisible }, false, "setErrorOverlayVisible"),
  setIsCasting: isCasting => set({ isCasting }, false, "setIsCasting"),
  setIsChatButtonEnabled: isChatButtonEnabled =>
    set({ isChatButtonEnabled }, false, "setIsChatButtonEnabled"),
  setIsControlsVisible: isControlsVisible =>
    set({ isControlsVisible }, false, "setIsControlsVisible"),
  setIsDraggingTimeline: isDraggingTimeline =>
    set({ isDraggingTimeline }, false, "setIsDraggingTimeline"),
  setIsFullscreen: isFullscreen =>
    set({ isFullscreen }, false, "setIsFullscreen"),
  setIsPictureInPicture: isPictureInPicture =>
    set({ isPictureInPicture }, false, "setIsPictureInPicture"),
  setPlayerUiType: playerUiType =>
    set({ playerUiType }, false, "setPlayerUiType"),
  setUiSize: uiSizeInPx =>
    set(state => {
      if (uiSizeInPx < UI_SIZE_XSMALL_BREAKPOINT) {
        state.uiSize = UI_SIZE.XSMALL;
      } else if (uiSizeInPx < UI_SIZE_SMALL_BREAKPOINT) {
        state.uiSize = UI_SIZE.SMALL;
      } else if (uiSizeInPx >= UI_SIZE_LARGE_BREAKPOINT) {
        state.uiSize = UI_SIZE.LARGE;
      } else {
        state.uiSize = UI_SIZE.DEFAULT;
      }
      state.uiSizeInPx = uiSizeInPx;
    }),
  setVisibleSettingsMenu: visibleSettingsMenu =>
    set({ visibleSettingsMenu }, false, "setVisibleSettingsMenu")
});
